import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import { entityRefToCatalogPath } from '../shared/entityRefs';
import { useManagerChain } from '../shared/useManagerChain';

/**
 * Renders the upward manager chain from a starting user/manager ref as a
 * breadcrumb — e.g. "Sarah Olsson (CEO) → Mike Andersson (CTO) → Russell
 * Blaisdell (VP Engineering)" — with each name linking to that user's
 * entity page. Role is shown in a smaller muted typography alongside the
 * name.
 *
 * Data-fetch concern (async walk + cycle detection + depth cap) lives in
 * useManagerChain (shared/). This component is render-only.
 *
 * Renders semantically as a <nav> wrapping an <ol> of breadcrumb entries —
 * screen readers announce "Manager hierarchy" and treat the chain as an
 * ordered list. The "→" separators are aria-hidden decoration.
 *
 * Renders nothing if the starting ref is undefined (current user has no
 * manager annotation).
 */

const useStyles = makeStyles(theme => ({
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  entry: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  role: {
    color: theme.palette.text.disabled,
    fontSize: '0.8125rem',
  },
  separator: {
    color: theme.palette.text.disabled,
    userSelect: 'none',
  },
  truncated: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
}));

export interface EntityUserManagerChainProps {
  /** The user/manager annotation value on the entity being viewed. */
  initialManagerRef: string | undefined;
}

export function EntityUserManagerChain({
  initialManagerRef,
}: EntityUserManagerChainProps) {
  const classes = useStyles();
  const { chain, truncated } = useManagerChain(initialManagerRef);

  if (chain.length === 0) return null;

  return (
    <nav aria-label="Manager hierarchy">
      <Box component="ol" className={classes.list}>
        {truncated && (
          <Box component="li" className={classes.entry}>
            <Typography className={classes.truncated} component="span">
              …
            </Typography>
          </Box>
        )}
        {chain.map((entry, idx) => {
          // The very first item of the visible list — either the truncated
          // indicator (if present) or chain[0] — has no preceding arrow.
          const isFirstVisible = !truncated && idx === 0;
          return (
            <Box component="li" key={entry.ref} className={classes.entry}>
              {!isFirstVisible && (
                <Typography
                  className={classes.separator}
                  component="span"
                  aria-hidden="true"
                >
                  →
                </Typography>
              )}
              <Link to={entityRefToCatalogPath(entry.ref)}>
                {entry.displayName}
              </Link>
              {entry.role && (
                <Typography className={classes.role} component="span">
                  ({entry.role})
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </nav>
  );
}
